import React from "react";
import Modal from "react-modal";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import styles from "./styles.module.scss";
import clsx from "clsx";
import { FitTwoLines } from "./fit-two-lines";
import { Screen2CardMemo, Screen2CardTitle, Screen2Video } from "./screen2-card";
import CtaCarousel from "../../ui/cta-carousel";
import { useCtaClick } from "../../ui/cta-carousel/use-cta-click";
import { useChatbotConfig } from "../../../contexts/chatbot-config";
import { useBottomSheet } from "../../../contexts/bottom-sheet";
import { IoChevronBack, IoClose } from "react-icons/io5";
import DOMPurify from "dompurify";
import { openCtaLink, trackLinkClick } from "../../../libs/fns";
import { buildCtaEntries, extractYoutubeId, normalizeSortNumber, resolveBucketAsset, type CtaEntry } from "../../../libs/cta";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

type Banner = {
  key: string;
  type: "link" | "file" | "pdf" | "youtube";
  title?: string;
  img_src: string;
  href?: string;
  videoId: string | null;
  memo?: string;
};

type BannerProps = {
  title: React.ReactNode;
  banners: Banner[];
  ctas: CtaEntry[];
};

/**
 * デフォルトのダミーデータ（APIデータがない場合のフォールバック）
 * 
 * API仕様: POST /api/initialize/
 * Response.screen2_data.banner_data: Array<{
 *   banner_sort: string,  // ソート順
 *   title: string,        // バナータイトル
 *   type: string,         // "link" | "file" | "pdf" | "youtube"
 *   link: string,         // リンク先URL
 *   image: string         // 画像URL
 * }>
 */
const defaultBannerProps: BannerProps = {
  title: (
    <>
      お役立ち情報
      <br />
      ぜひ、ご確認ください。
    </>
  ),
  ctas: [],
  banners: [],
};

export default function Aside() {
  const { screen2Data, screen2HeaderText, ctaData, chatbotData } = useChatbotConfig();
  const { setOpen: setBottomSheetOpen } = useBottomSheet();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const buttonWrapRef = React.useRef<HTMLDivElement>(null);
  // Screen2ボタン振動アニメーション管理
  const [buttonShaking, setButtonShaking] = React.useState(false);
  // ダウンロード中トースト管理
  const [downloadingBannerId, setDownloadingBannerId] = React.useState<string | null>(null);

  // PDF Modal state
  const [pdfModalOpen, setPdfModalOpen] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState<string>("");
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [modalRoot, setModalRoot] = React.useState<HTMLElement | null>(null);
  const [_pdfLoading, setPdfLoading] = React.useState(false);
  const pdfCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const pdfDocRef = React.useRef<PDFDocumentProxy | null>(null);

  React.useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const currentRoot = containerRef.current.getRootNode();
    const shadowRoot = currentRoot instanceof ShadowRoot ? currentRoot : null;

    if (!shadowRoot) {
      return;
    }

    // モーダル用のポータルDOMを作成
    let portal = shadowRoot.querySelector<HTMLElement>("#aside-pdf-modal-portal");
    if (!portal) {
      portal = document.createElement("div");
      portal.id = "aside-pdf-modal-portal";
      shadowRoot.appendChild(portal);
    }

    setModalRoot(portal);

    return () => {
      // クリーンアップ: モーダルが閉じられたらポータルを削除
      if (portal && shadowRoot.contains(portal) && !pdfModalOpen) {
        try {
          shadowRoot.removeChild(portal);
        } catch (e) {
          // Error silently handled
        }
      }
    };
  }, [pdfModalOpen]);

  // resol_metis /chat の screen2Banners / screen2Ctas と同一の正規化
  const bannerProps: BannerProps = React.useMemo(() => {
    const baseUrl = chatbotData?.chatbot_S3_URL || "";
    const ctas = buildCtaEntries(ctaData, baseUrl);

    const legacyTitle = screen2Data && !Array.isArray(screen2Data) ? screen2Data.title : undefined;
    const title = screen2HeaderText
      ? <div>{screen2HeaderText}</div>
      : ctaData?.title
        ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ctaData.title) }} />
        : legacyTitle || defaultBannerProps.title;

    type RawItem = {
      sort_num?: number | string;
      banner_sort?: number | string;
      title?: string;
      type?: string | number;
      link?: string;
      image?: string;
      image_path?: string;
      file_path?: string;
      memo?: string;
    };
    let items: RawItem[] = [];
    if (Array.isArray(screen2Data)) {
      items = screen2Data;
    } else if (screen2Data && Array.isArray(screen2Data.banner_data)) {
      items = screen2Data.banner_data;
    }

    const banners: Banner[] = items
      .filter((item): item is RawItem => !!item)
      .sort((a, b) => normalizeSortNumber(a.sort_num ?? a.banner_sort) - normalizeSortNumber(b.sort_num ?? b.banner_sort))
      .map((item, index) => {
        const imageSrc = resolveBucketAsset(item.image_path || item.image || null, baseUrl);
        const loweredType = (typeof item.type === "number" ? String(item.type) : (item.type || "link")).toLowerCase();
        let type: Banner["type"] = "link";
        if (loweredType === "download" || loweredType === "file") type = "file";
        else if (loweredType === "pdf") type = "pdf";
        else if (loweredType === "youtube") type = "youtube";
        let href = item.link || undefined;
        if ((type === "pdf" || type === "file") && item.file_path) {
          href = resolveBucketAsset(item.file_path, baseUrl);
        } else if ((type === "pdf" || type === "file") && href && !href.startsWith("http")) {
          href = resolveBucketAsset(href, baseUrl);
        }
        return {
          key: `${item.title || "banner"}-${index}`,
          type,
          title: item.title || undefined,
          img_src: imageSrc,
          href,
          videoId: type === "youtube" ? extractYoutubeId(item.link || item.file_path || item.image || undefined) : null,
          memo: item.memo || undefined,
        };
      })
      .filter((banner) => (banner.type === "youtube" ? !!banner.videoId : !!banner.img_src || !!banner.href));

    return { title, ctas, banners };
  }, [screen2Data, screen2HeaderText, ctaData, chatbotData]);

  const hasScreen2Content = bannerProps.banners.length > 0 || bannerProps.ctas.length > 0;

  React.useEffect(() => {
    function handleSet() {
      if (!buttonWrapRef.current || !containerRef.current) return;
      const height = containerRef.current.clientHeight;

      buttonWrapRef.current.style.height = height + "px";
    }
    handleSet();
    window.addEventListener("resize", handleSet);
    return () => window.removeEventListener("resize", handleSet);
  }, [open]);

  // Screen2ボタン振動タイマー: 3秒後に初回、以降10秒おきに振動
  React.useEffect(() => {
    if (!hasScreen2Content || open) return;

    const triggerShake = () => {
      setButtonShaking(true);
      setTimeout(() => setButtonShaking(false), 700);
    };

    const initialTimer = setTimeout(triggerShake, 3000);
    const interval = setInterval(triggerShake, 10000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [hasScreen2Content, open]);

  function handleOpen() {
    // BottomSheetが開いている場合は閉じてからScreen2を開く
    setBottomSheetOpen(false);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  React.useEffect(() => {
    if (!hasScreen2Content) setOpen(false);
  }, [hasScreen2Content]);

  const handleCtaClick = useCtaClick(handleClose);

  function handleBannerClick(banner: Banner) {
    if (!banner.href) return;

    if (banner.type === "pdf") {
      setPdfUrl(banner.href);
      setPdfModalOpen(true);
      setPageNumber(1);
      trackLinkClick(banner.href).catch(() => {});
      return;
    }

    if (banner.type === "file") {
      const fileUrl = banner.href;
      const fileName = fileUrl.split("/").pop() || "download";
      trackLinkClick(fileUrl).catch(() => {});
      setDownloadingBannerId(banner.key);
      fetch(fileUrl, { mode: "cors", credentials: "omit" })
        .then((response) => response.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = decodeURIComponent(fileName);
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
        })
        .catch(() => {
          window.open(fileUrl, "_blank");
        })
        .finally(() => {
          setDownloadingBannerId(null);
        });
      return;
    }

    const safeHref = /^(tel:|mailto:|sms:|https?:\/\/)/i.test(banner.href) ? banner.href : `https://${banner.href}`;
    openCtaLink(safeHref);
    trackLinkClick(safeHref).catch(() => {});
  }

  function closePdfModal() {
    setPdfModalOpen(false);
    setPdfUrl("");
    setPageNumber(1);
    setNumPages(null);
    if (pdfDocRef.current) {
      pdfDocRef.current.destroy();
      pdfDocRef.current = null;
    }
  }

  function goToPrevPage() {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }

  function goToNextPage() {
    setPageNumber((prev) => Math.min(prev + 1, numPages || prev));
  }

  // Load and render PDF
  React.useEffect(() => {
    if (!pdfUrl || !pdfModalOpen) return;

    // pdfUrlが素早く連続変更された場合、前のロードがまだ解決していない状態で
    // 新しいロードが始まることがある。cancelledガードが無いと、後から解決した
    // 古いPDFのロード結果がpdfDocRef/numPagesを新しいPDFのもので上書きして
    // しまう（表示中のPDFと内部状態が食い違う）。
    let cancelled = false;

    const loadAndRenderPDF = async () => {
      try {
        setPdfLoading(true);

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) {
          pdf.destroy();
          return;
        }
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setPdfLoading(false);
      } catch (error) {
        if (!cancelled) {
          setPdfLoading(false);
        }
      }
    };

    loadAndRenderPDF();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [pdfUrl, pdfModalOpen]);

  // Render current page
  React.useEffect(() => {
    if (!pdfDocRef.current || !pdfCanvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDocRef.current!.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min(window.innerWidth * 0.8, 800) / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = pdfCanvasRef.current!;
        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = scaledViewport.width * window.devicePixelRatio;
        canvas.height = scaledViewport.height * window.devicePixelRatio;
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;

        const transform = window.devicePixelRatio !== 1
          ? [window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0]
          : undefined;

        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
          transform: transform,
        }).promise;
      } catch (error) {
        // Error silently handled
      }
    };

    renderPage();
  }, [pageNumber, pdfDocRef.current]);

  return (
    <>
      {hasScreen2Content && (
        <>
          <div className={styles.buttonWrap}>
            <button
              className={clsx(styles.button, !open && buttonShaking && styles.buttonShaking)}
              type="button"
              onClick={open ? handleClose : handleOpen}
              aria-label={open ? "Close sidebar" : "Open sidebar"}
            >
              {open ? <IoClose size={24} /> : <IoChevronBack size={24} />}
            </button>
          </div>
          <div className={clsx(styles.overlay, open && styles.overlayOpen)} />
          <div className={clsx(styles.aside, open && styles.open)}>
            {/* ダウンロード中トースト */}
            <div style={{
              position: 'sticky',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              height: 0,
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: '16px',
                backgroundColor: 'rgba(0, 0, 0, 0.72)',
                color: '#fff',
                padding: '10px 22px',
                borderRadius: '24px',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                opacity: downloadingBannerId ? 1 : 0,
                transform: downloadingBannerId ? 'translateY(0)' : 'translateY(-8px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: 'none'
              }}>
                資料をダウンロードしています…
              </div>
            </div>
            <div className={styles.container} ref={containerRef}>
              <FitTwoLines className={styles.lead}>{bannerProps.title}</FitTwoLines>
              {bannerProps.ctas.length > 0 && (
                <CtaCarousel ctas={bannerProps.ctas} onCtaClick={handleCtaClick} />
              )}
              {bannerProps.banners.length > 0 && (
                <ul className={styles.bannerList}>
                  {bannerProps.banners.map((banner) => (
                    <li key={banner.key} className={styles.bannerItem}>
                      {banner.type === "youtube" && banner.videoId ? (
                        <div className={styles.bannerCard}>
                          <Screen2CardTitle title={banner.title} />
                          <div style={{ padding: "6px 12px 12px", backgroundColor: "#fff" }}>
                            <Screen2Video videoId={banner.videoId} />
                          </div>
                          <Screen2CardMemo memo={banner.memo} />
                        </div>
                      ) : (
                        <div className={styles.bannerCard} style={{ position: "relative", width: "100%" }}>
                          <Screen2CardTitle title={banner.title} />
                          <div style={{ position: "relative" }}>
                            {banner.img_src ? (
                              <img
                                src={banner.img_src}
                                alt={banner.title || "Screen2 banner"}
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              />
                            ) : (
                              <div style={{ display: "block", padding: "20px", fontWeight: 600 }}>リンクを開く</div>
                            )}
                            {banner.href && (
                              <div style={{ position: "absolute", bottom: "12px", right: "12px", zIndex: 1 }}>
                                <button
                                  type="button"
                                  className={styles.bannerButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBannerClick(banner);
                                  }}
                                >
                                  {banner.type === "pdf" ? "PDF" : banner.type === "file" ? "Download" : "Click"}
                                </button>
                              </div>
                            )}
                          </div>
                          <Screen2CardMemo memo={banner.memo} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* PDF Modal - modalRootが準備できてから表示 */}
              {modalRoot && (
                <Modal
                  isOpen={pdfModalOpen}
                  onRequestClose={closePdfModal}
                  parentSelector={() => modalRoot}
                  ariaHideApp={false}
                  style={{
                    overlay: {
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.75)",
                      zIndex: 99999,
                    },
                    content: {
                      position: "relative",
                      top: "50%",
                      left: "50%",
                      right: "auto",
                      bottom: "auto",
                      marginRight: "-50%",
                      transform: "translate(-50%, -50%)",
                      padding: 0,
                      borderRadius: "8px",
                      maxWidth: "90vw",
                      maxHeight: "90vh",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      display: "flex",
                      flexDirection: "column",
                    },
                  }}
                >
                  {/* PDF Header */}
                  <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexShrink: 0,
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#333",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "60%",
                    }}>
                      {pdfUrl ? decodeURIComponent(pdfUrl.split('/').pop() || 'PDF') : 'PDF'}
                    </h3>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <a
                        href={pdfUrl || "#"}
                        download
                        className={styles.pdfDownloadBtn}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span className={styles.btnText}>ダウンロード</span>
                      </a>
                      <button
                        onClick={closePdfModal}
                        className={styles.pdfCloseBtn}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        <span className={styles.btnText}>閉じる</span>
                      </button>
                    </div>
                  </div>

                  {/* PDF Content */}
                  <div style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}>
                    <div style={{ marginBottom: "10px", display: "flex", gap: "10px", alignItems: "center" }}>
                      <button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        style={{
                          padding: "8px 16px",
                          cursor: pageNumber <= 1 ? "not-allowed" : "pointer",
                          backgroundColor: pageNumber <= 1 ? "#ccc" : "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      >
                        前へ
                      </button>
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        {pageNumber} / {numPages || "..."}
                      </span>
                      <button
                        onClick={goToNextPage}
                        disabled={pageNumber >= (numPages || 1)}
                        style={{
                          padding: "8px 16px",
                          cursor: pageNumber >= (numPages || 1) ? "not-allowed" : "pointer",
                          backgroundColor: pageNumber >= (numPages || 1) ? "#ccc" : "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      >
                        次へ
                      </button>
                    </div>
                    <canvas
                      ref={pdfCanvasRef}
                      style={{
                        display: 'block',
                        maxWidth: '100%',
                        height: 'auto',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                </Modal>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
